import { immer } from 'zustand/middleware/immer'
import { createWithEqualityFn as create } from 'zustand/traditional'
import _isAllowed from './allowed'
import { safeParseJson } from './safe-json'
import type {
	AuthProviderState,
	AuthUser,
	AuthUserResponse,
	Endpoints,
	ParseResponse,
} from './types'
import { setUserState } from './user-state'

export const makeCreateStore =
	(
		baseURL: string,
		endpoints: Endpoints,
		parseResponse: ParseResponse<AuthUserResponse>,
		_mode: 'legacy' | 'service' = 'legacy',
	) =>
	() => {
		async function getAuthorizationUrl() {
			const authServiceUrl = endpoints.getAuthServiceUrl()

			const res = await fetch(authServiceUrl, {
				method: 'get',
				credentials: 'include',
			})

			if (!res.ok) {
				return ''
			}

			const result = await safeParseJson(res, 'getAuthorizationUrl')
			const { authorizationUrl } = (result.data as any) ?? {}

			return authorizationUrl ?? ''
		}

		const store = create<AuthProviderState<AuthUser>>()(
			immer((set, get) => ({
				getAuthorizationUrl: getAuthorizationUrl,

				forgotPassword(email) {
					const url = endpoints?.forgotPassword({ baseURL })

					return fetch(url, {
						method: 'POST',
						body: JSON.stringify({ email }),
					})
				},

				async resetPassword(values) {
					const url = endpoints.resetPassword({ baseURL })

					return await fetch(url, {
						method: 'POST',
						body: JSON.stringify(values),
					})
				},

				async authenticate(accountId) {
					const url = endpoints.getUser({ baseURL, accountId })
					const res = await fetch(url, {
						method: 'GET',
						credentials: 'include',
					}).catch((e) => {
						console.log(e)
					})

					if (!res?.ok) {
						// sessionStorage.setItem('state', window.location.hash)
						//
						// window.location.replace(url)
						const newState = {
							loggedIn: false,
							tried: true,
							authServiceUrl: '',
							forbidden: res?.status === 403,
						} as const

						set((state) => {
							state.user = newState
						})

						return newState as unknown as AuthUser
					}

					// const appState = sessionStorage.getItem('state')
					//
					// if (appState) {
					// }

					const newState = await setUserState(parseResponse, res, set)

					if (!newState) {
						const fallback = {
							loggedIn: false,
							tried: true,
							authServiceUrl: '',
							forbidden: false,
						} as const

						set((state) => {
							state.user = fallback
						})

						return fallback as unknown as AuthUser
					}

					return newState as AuthUser
				},

				async signIn(form) {
					const url = endpoints.login({ baseURL })

					const res = await fetch(url, {
						method: 'POST',
						credentials: 'include',
						body: JSON.stringify({
							email: form.email.value,
							password: form.password.value,
						}),
					}).catch((e) => {
						console.log(e)
					})

					const { status } = res || { status: 500 }

					if (!res || status !== 200) {
						throw new Error(
							[404, 500].indexOf(status) > -1
								? 'Server error'
								: 'You have entered an invalid email or password',
						)
					}

					const newState = await setUserState(parseResponse, res, set)

					if (!newState) {
						throw new Error('Server error')
					}

					return newState
				},

				signOut(silent?: boolean) {
					const url = endpoints.logout({ baseURL })

					return fetch(url, {
						method: 'POST',
						credentials: 'include',
					})
						.then(async () => {
							if (silent) {
								return
							}

							set((state) => {
								state.user = {
									loggedIn: false,
									tried: true,
									authServiceUrl: '',
									forbidden: false,
								}
							})
						})
						.catch(() => {})
				},

				isAllowed({ isAdminAllowed = true, allowed = 'NA' }) {
					const user = get().user
					const isAdmin = user.loggedIn ? user.isAdmin : false
					const roles = user.loggedIn ? user.roles : []

					return _isAllowed({
						is: { isAdmin, roles },
						should: { isAdminAllowed, allowed },
					})
				},

				user: {
					loggedIn: false,
					tried: false,
					forbidden: false,
					authServiceUrl: '',
				},
			})),
		)

		store.getState().authenticate()

		return store
	}
